/**
 * Celebrity opponent domain model.
 *
 * Celebrity data lives on the backend (D1) so changes made by an admin apply
 * to all users. The frontend only fetches the list; it never edits it.
 */

export type CelebrityDifficulty = "easy" | "medium" | "hard" | "beast";

export interface VoicePhase {
  id: string;
  /** Human-readable label shown in the admin UI (e.g. "Taunt 1"). */
  name: string;
  /** base64 data URL of the audio clip (mono WAV, re-encoded by the admin form). */
  audioDataUrl: string;
  /** Sort order for playback. */
  order: number;
}

export interface Celebrity {
  id: string;
  name: string;
  /** base64 data URL of the profile photo. */
  imageDataUrl: string;
  difficulty: CelebrityDifficulty;
  /** How many seconds per rep the AI opponent does (lower = harder). */
  secondsPerRep: number;
  voicePhases: VoicePhase[];
  /** Round length in seconds. 0 = no time limit (keep going until stopped). */
  workoutDurationSec: number;
}

/** Payload for creating or updating a celebrity (everything except `id`). */
export type CelebrityInput = Omit<Celebrity, "id">;

/**
 * Read-only roster preview served to everyone (including signed-out users) so
 * the opponent circles can render without exposing coaching data. Deliberately
 * omits `difficulty`, `secondsPerRep`, `voicePhases`, and `workoutDurationSec`,
 * which only the authenticated celebrity list carries.
 */
export interface PublicCelebrity {
  id: string;
  name: string;
  imageDataUrl: string;
}

/**
 * Maps difficulty levels to a default seconds-per-rep pacing value.
 * Used when creating a new celebrity — can be overridden per-celebrity.
 */
export const DIFFICULTY_PACING: Record<CelebrityDifficulty, number> = {
  easy: 8,
  medium: 5,
  hard: 3,
  beast: 2,
};

export const DIFFICULTY_LABELS: Record<CelebrityDifficulty, string> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  beast: "Beast",
};

export const ALL_DIFFICULTIES: CelebrityDifficulty[] = ["easy", "medium", "hard", "beast"];
